import { Link as RouterLink } from "react-router";

import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Link as MuiLink, TableCell, TableRow } from "@mui/material";
import { format } from "date-fns";

import { ClientSearchResult } from "@/api";
import { EllipsisText } from "@/common/components/ellipsis-text/EllipsisText";
import { useLanguageContext } from "@/common/contexts/language-context/LanguageContext";
import { locales } from "@/common/contexts/localization-date-context/LocalizationDateContextProvider.utils";
import { ClientMasterDataTab, clientPath } from "@/router/ClientRoutes";
import { useTranslation } from "@/translations";

import { useStyles } from "../ClientDuplicateWarning.styles";

interface Props {
  client: ClientSearchResult;
}

function formatAddress(client: ClientSearchResult): string {
  const streetLine = [client.street, client.houseNumber].filter(Boolean).join(" ");
  const cityLine = [client.zipCode, client.city].filter(Boolean).join(" ");
  return [streetLine, cityLine].filter(Boolean).join(", ");
}

export function DuplicateRow({ client }: Props) {
  const { t } = useTranslation();
  const { language } = useLanguageContext();
  const classes = useStyles();
  // Full nested personal-data path so a cold tab load resolves directly instead of relying on index
  // redirects. RouterLink applies the configured basename automatically.
  const path = clientPath.byMasterDataTab({ id: client.clientId, tab: ClientMasterDataTab.PersonalData });

  return (
    <TableRow hover sx={classes.rowLink}>
      <TableCell>
        <MuiLink
          component={RouterLink}
          to={path}
          target="_blank"
          rel="noopener noreferrer"
          sx={classes.rowOverlayLink}
          aria-label={t("clients.create.duplicateOpenInNewTab", {
            name: `${client.firstName} ${client.lastName}`,
          })}
        />
        <EllipsisText value={client.firstName} variant="body2" />
      </TableCell>
      <TableCell>
        <EllipsisText value={client.lastName} variant="body2" />
      </TableCell>
      <TableCell>
        <EllipsisText
          value={client.birthday ? format(client.birthday, "P", { locale: locales[language] }) : ""}
          variant="body2"
        />
      </TableCell>
      <TableCell>
        <EllipsisText value={client.caseNumber} variant="body2" />
      </TableCell>
      <TableCell>
        <EllipsisText value={formatAddress(client)} variant="body2" />
      </TableCell>
      <TableCell padding="checkbox">
        <ChevronRightIcon fontSize="small" />
      </TableCell>
    </TableRow>
  );
}
